import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DAY_MS = 86400000;

export default function MyCalendar() {
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });
  const [data, setData] = useState({ tasks: [], shifts: [], incidents: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    const from = fmt(monthAnchor);
    const last = new Date(monthAnchor); last.setMonth(last.getMonth() + 1); last.setDate(0);
    const to = fmt(last);
    api.get("/calendar", { params: { from_date: from, to_date: to } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [monthAnchor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    const push = (day, ev) => { (map[day] = map[day] || []).push(ev); };
    data.tasks.forEach((t) => {
      const d = (t.activity_date || t.due_date || "").slice(0, 10);
      if (d) push(d, { type: "task", ...t });
    });
    data.shifts.forEach((s) => push((s.clock_in || s.scheduled_start || "").slice(0, 10), { type: "shift", ...s }));
    data.incidents.forEach((i) => push((i.incident_date || "").slice(0, 10), { type: "incident", ...i }));
    return map;
  }, [data]);

  const grid = buildGrid(monthAnchor);
  const monthLabel = monthAnchor.toLocaleDateString("en-AU", { month: "long", year: "numeric" });

  const totalTaskMinutes = data.tasks.reduce((s, t) => s + (t.duration_minutes || 0), 0);
  const totalShiftMinutes = data.shifts.reduce((s, sh) => s + (sh.duration_minutes || 0), 0);

  return (
    <div className="fade-in" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">Timeline</div>
          <h2 className="font-serif text-3xl text-njas-navy">My Calendar</h2>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="text-njas-muted">Task hours: <strong className="text-njas-navy">{(totalTaskMinutes / 60).toFixed(1)}</strong></div>
          <div className="text-njas-muted">Shift hours: <strong className="text-njas-navy">{(totalShiftMinutes / 60).toFixed(1)}</strong></div>
        </div>
      </div>

      <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--njas-border)]">
          <button onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))} className="p-1 rounded hover:bg-njas-bg-alt"><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-serif text-lg text-njas-navy">{monthLabel}</div>
          <button onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))} className="p-1 rounded hover:bg-njas-bg-alt"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 text-xs text-njas-muted uppercase tracking-wider text-center bg-njas-bg-alt/40 border-b border-[var(--njas-border)]">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((cell, i) => {
            const iso = cell ? fmt(cell) : null;
            const events = iso ? (eventsByDay[iso] || []) : [];
            const isToday = iso === fmt(new Date());
            const inMonth = cell && cell.getMonth() === monthAnchor.getMonth();
            return (
              <button key={i} onClick={() => iso && setSelected(iso)}
                className={`min-h-[100px] border-b border-r border-[var(--njas-border)] p-2 text-left align-top transition-colors ${
                  !inMonth ? "bg-njas-bg-alt/40 text-njas-muted" : "bg-white hover:bg-njas-bg-alt/40"
                } ${isToday ? "ring-2 ring-njas-sage ring-inset" : ""}`}>
                <div className="text-xs font-medium mb-1">{cell?.getDate()}</div>
                <div className="space-y-0.5" onClick={(e) => e.stopPropagation()}>
                  {events.slice(0, 3).map((ev) => (
                    <div key={`${ev.type}-${ev.id}`}
                      onClick={(e) => { e.stopPropagation(); setSelected(iso); }}
                      className={`cursor-pointer text-[10px] px-1.5 py-0.5 rounded truncate hover:opacity-80 ${
                      ev.type === "task" ? "bg-njas-sage-tint text-njas-sage-hover" :
                      ev.type === "shift" ? "bg-blue-50 text-blue-700" :
                      "bg-red-50 text-red-700"
                    }`}>{ev.type === "task" ? ev.title : ev.type === "shift" ? "Shift" : "Incident"}</div>
                  ))}
                  {events.length > 3 && <div className="text-[10px] text-njas-muted">+{events.length - 3} more</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-sm text-njas-muted mt-3">Loading…</div>}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl text-njas-navy mb-2">{new Date(selected).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h3>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {(eventsByDay[selected] || []).length === 0 && <div className="text-sm text-njas-muted">Nothing on this day.</div>}
              {(eventsByDay[selected] || []).map((ev) => (
                <div key={`${ev.type}-${ev.id}`} className="rounded-lg border border-[var(--njas-border)] p-3 text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
                      ev.type === "task" ? "bg-njas-sage-tint text-njas-sage-hover" :
                      ev.type === "shift" ? "bg-blue-50 text-blue-700" :
                      "bg-red-50 text-red-700"
                    }`}>{ev.type}</span>
                    {ev.type === "task" && <span className="font-medium">{ev.title}</span>}
                  </div>
                  {ev.type === "task" && (
                    <div className="text-xs text-njas-muted space-y-1">
                      {ev.duration_minutes ? <div>Duration: {Math.floor(ev.duration_minutes / 60)}h {ev.duration_minutes % 60}m</div> : null}
                      {ev.funding_source && <div>Funding: <span className="capitalize">{ev.funding_source}</span>{ev.funding_details ? ` — ${ev.funding_details}` : ""}</div>}
                      {ev.client_id && <Link to={`/dashboard/clients/${ev.client_id}`} className="text-njas-navy hover:underline block">Client: {ev.client_name}</Link>}
                      {ev.description && <div className="text-njas-text whitespace-pre-wrap">{ev.description}</div>}
                    </div>
                  )}
                  {ev.type === "shift" && (
                    <div className="text-xs text-njas-muted">
                      {new Date(ev.clock_in).toLocaleTimeString("en-AU")}{ev.clock_out ? ` – ${new Date(ev.clock_out).toLocaleTimeString("en-AU")}` : " (active)"}
                      {ev.duration_minutes != null && <span> · {Math.floor(ev.duration_minutes / 60)}h {ev.duration_minutes % 60}m</span>}
                      {ev.notes && <div className="whitespace-pre-wrap">{ev.notes}</div>}
                    </div>
                  )}
                  {ev.type === "incident" && (
                    <div className="text-xs text-njas-muted">
                      <span className="capitalize">{ev.incident_type?.replace(/_/g, " ")}</span> · <span className="capitalize">{ev.severity}</span>
                      <div className="text-njas-text mt-1 whitespace-pre-wrap">{ev.description}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmt(d) {
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function buildGrid(anchor) {
  const first = new Date(anchor);
  const startWeekday = (first.getDay() + 6) % 7; // Monday=0
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(new Date(first.getTime() - (startWeekday - i) * DAY_MS));
  const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  for (let d = 1; d <= monthEnd.getDate(); d++) cells.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(new Date(cells[cells.length - 1].getTime() + DAY_MS));
  return cells;
}
